"use client"

import * as React from "react"
import {
  Phone,
  Mail,
  Clock,
  MapPin,
  Truck,
  CreditCard,
  RotateCcw,
  ShieldCheck,
  HelpCircle,
  Instagram,
  Send,
  FileText,
  Wallet,
  ArrowUp,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { useNavigationStore } from "@/store/use-navigation-store"
import { toast } from "sonner"
import { CONTACTS } from "@/lib/contacts"

const BUYER_LINKS = [
  { label: "Доставка", icon: Truck, page: "delivery" as const },
  { label: "Оплата", icon: CreditCard, page: "delivery" as const },
  { label: "Возврат", icon: RotateCcw, page: "delivery" as const },
  { label: "Гарантия", icon: ShieldCheck, page: "about" as const },
  { label: "FAQ", icon: HelpCircle, page: "faq" as const },
] as const

const CATALOG_LINKS = [
  "Межкомнатные двери",
  "Входные двери",
  "Фурнитура",
  "Наличники и доборы",
  "Арки",
  "Порталы",
] as const

export function Footer() {
  const navigate = useNavigationStore((s) => s.navigate)
  const [email, setEmail] = React.useState("")

  const handleSubscribe = () => {
    if (!email.trim()) return
    toast.success("Вы подписаны!")
    setEmail("")
  }

  return (
    <footer className="mt-auto border-t bg-foreground text-background">
      {/* Top accent line - animated */}
      <div className="h-1 gradient-line-animated" />

      {/* Subtle gradient fade at top of footer */}
      <div className="h-16 bg-gradient-to-b from-brand/5 to-transparent" />

      {/* Trust bar */}
      <div className="bg-foreground/95 border-b border-background/5 -mt-16 relative z-10">
        <div className="container mx-auto px-4 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <Truck className="size-5 text-primary" />
              <div className="text-left">
                <p className="text-sm font-medium">Бесплатная доставка</p>
                <p className="text-xs text-background/50">При заказе от 15 000 ₽</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2">
              <ShieldCheck className="size-5 text-primary" />
              <div className="text-left">
                <p className="text-sm font-medium">Гарантия до 5 лет</p>
                <p className="text-xs text-background/50">От производителя</p>
              </div>
            </div>
              <div className="flex items-center justify-center gap-2">
               <Phone className="size-5 text-primary" />
               <div className="text-left">
                 <p className="text-sm font-medium">{CONTACTS.hours.weekdayRange}</p>
                 <p className="text-xs text-background/50">{CONTACTS.phoneLabel} / {CONTACTS.phone2}</p>
               </div>
             </div>
          </div>
        </div>
      </div>

      {/* Main footer content */}
      <div className="container mx-auto px-4 py-10">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {/* Column 1: Logo & description */}
          <div className="space-y-4">
            <button
              onClick={() => navigate("home")}
              className="inline-flex items-center gap-2 hover:scale-105 hover:drop-shadow-[0_0_15px_rgba(180,100,50,0.2)] transition-transform"
            >
              <div className="flex items-center justify-center size-9 rounded-lg bg-primary text-primary-foreground">
                <span className="text-base font-black">Д</span>
              </div>
              <span className="text-xl font-bold tracking-tight">
                Двери<span className="text-primary/70">Ант</span>
              </span>
            </button>
            <p className="text-sm leading-relaxed text-background/60 max-w-xs">
              Интернет-магазин межкомнатных и входных дверей в Севастополе.
              Большой выбор от ведущих производителей России и мира.
              Профессиональная доставка и установка.
            </p>
            {/* Social links */}
            <div className="flex items-center gap-2.5">
              <a
                href={CONTACTS.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex size-9 items-center justify-center rounded-full bg-background/8 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-500 hover:text-white social-hover transition-all duration-200"
                aria-label="Instagram"
              >
                <Instagram className="size-4" />
              </a>
              <a
                href={CONTACTS.social.telegram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex size-9 items-center justify-center rounded-full bg-background/8 hover:bg-sky-500 hover:text-white social-hover transition-all duration-200"
                aria-label="Telegram"
              >
                <Send className="size-4" />
              </a>
              <a
                href={CONTACTS.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex size-9 items-center justify-center rounded-full bg-background/8 hover:bg-green-600 hover:text-white social-hover transition-all duration-200"
                aria-label="WhatsApp"
              >
                <Phone className="size-4" />
              </a>
            </div>
            {/* Newsletter */}
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium">Подписка на акции</p>
              <div className="flex gap-2">
                <Input
                  placeholder="Ваш email"
                  className="h-8 text-xs bg-background/10 border-background/20 text-background newsletter-glow placeholder:text-background/40"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
                />
                <Button size="icon" className="h-8 w-8 shrink-0 bg-primary hover:bg-primary/90" onClick={handleSubscribe}>
                  <Send className="size-3.5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Column 2: Catalog categories */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-background/40">
              Каталог
            </h3>
            <div className="h-0.5 w-12 bg-primary rounded-full mt-2" />
            <ul className="space-y-2.5">
              {CATALOG_LINKS.map((label) => (
                <li key={label}>
                  <button
                    onClick={() => navigate("catalog")}
                    className="text-sm text-background/60 hover:text-background transition-colors"
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Buyer info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-background/40">
              Покупателям
            </h3>
            <div className="h-0.5 w-12 bg-primary rounded-full mt-2" />
            <ul className="space-y-2.5">
              {BUYER_LINKS.map((link) => {
                const Icon = link.icon
                return (
                  <li key={link.label}>
                    <button
                      onClick={() => navigate(link.page)}
                      className="flex items-center gap-2 text-sm text-background/60 hover:text-background transition-colors"
                    >
                      <Icon className="size-3.5" />
                      {link.label}
                    </button>
                  </li>
                )
              })}
            </ul>
            <div className="pt-2">
              <button
                onClick={() => navigate("about")}
                className="flex items-center gap-2 text-sm text-background/60 hover:text-background transition-colors"
              >
                <FileText className="size-3.5" />
                Политика конфиденциальности
              </button>
            </div>
          </div>

          {/* Column 4: Contacts */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-background/40">
              Контакты
            </h3>
            <div className="h-0.5 w-12 bg-primary rounded-full mt-2" />
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 size-4 shrink-0 text-primary/60" />
                <span className="text-background/60">
                  {CONTACTS.address.full}
                </span>
              </li>
              <li>
                <a
                  href={`tel:${CONTACTS.phoneRaw}`}
                  className="flex items-center gap-2.5 text-background/60 hover:text-background transition-colors font-medium"
                >
                  <Phone className="size-4 shrink-0 text-primary/60" />
                  {CONTACTS.phoneLabel}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${CONTACTS.phone2Raw}`}
                  className="flex items-center gap-2.5 text-background/60 hover:text-background transition-colors font-medium"
                >
                  <Phone className="size-4 shrink-0 text-primary/60" />
                  {CONTACTS.phone2}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${CONTACTS.email}`}
                  className="flex items-center gap-2.5 text-background/60 hover:text-background transition-colors"
                >
                  <Mail className="size-4 shrink-0 text-primary/60" />
                  {CONTACTS.email}
                </a>
              </li>
              <li className="flex items-center gap-2.5 text-background/60">
                <Clock className="size-4 shrink-0 text-primary/60" />
                {CONTACTS.hours.weekdayRange}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="footer-gradient-sep">
        <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-background/40 text-center sm:text-left">
            © {new Date().getFullYear()} ДвериАнт. Все права защищены.
          </p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 text-xs text-background/30">
              <span className="flex items-center gap-1">
                <Wallet className="size-3" />
                Наличные
              </span>
              <span className="text-background/10">|</span>
              <span className="flex items-center gap-1">
                <CreditCard className="size-3" />
                Перевод
              </span>
            </div>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center gap-1 text-xs text-background/40 hover:text-background transition-colors"
            >
              <ArrowUp className="size-3" />
              Наверх
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}
